import { useEffect, useState } from 'react';

import { ProgressEvent } from '../types/progress';

const API_URL: string = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
const TERMINAL = new Set<string>(['done', 'error']);

export interface ProgressHandlers {
  onDone?: (event: ProgressEvent) => void;
  onError?: (event: ProgressEvent) => void;
}

/**
 * Subscribe to a post's publish progress via SSE.
 * Returns the latest event or null. Closes automatically on terminal
 * phase or when postId clears.
 */
export default function usePostProgress(
  postId: number | null,
  { onDone, onError }: ProgressHandlers = {},
): ProgressEvent | null {
  const [event, setEvent] = useState<ProgressEvent | null>(null);

  useEffect(() => {
    if (!postId) {
      setEvent(null);
      return undefined;
    }
    const url = `${API_URL}/posts/posts/${postId}/progress/`;
    const es = new EventSource(url, { withCredentials: true });

    es.onmessage = (msg) => {
      let data: ProgressEvent;
      try { data = JSON.parse(msg.data) as ProgressEvent; } catch { return; }
      setEvent(data);
      if (TERMINAL.has(data.phase)) {
        es.close();
        if (data.phase === 'done' && onDone) onDone(data);
        if (data.phase === 'error' && onError) onError(data);
      }
    };

    es.onerror = () => {
      // Browser auto-retries via the server's retry: directive until close().
    };

    return () => es.close();
  }, [postId, onDone, onError]);

  return event;
}
